import React, { useCallback, useMemo, useState } from 'react';
import { FieldFilter } from '@components/FieldFilter';
import { FieldFilterBoxSelectExpression } from '@components/filters/FieldFilterBoxSelectExpression';
import {
  Filter,
  filterOperatorOptions,
  filterToRawExpression,
  isTopKFilter,
  MultiSelectTags,
  OptionObject,
  SortDir,
  TextInput,
} from '@fabric-ui/components';
import { ArrowDown } from '@fabric-ui/icons';
import { ellipsis } from '@fabric-ui/theme';
import { noop } from '@fabric-ui/utils';
import { useAvContext } from '../../context/AvContextProvider';
import TopKDisplay from './components/TopKDisplay';

interface FieldFilterBoxSelectProps {
  fieldName?: string | null;
  size: 'xSmall' | 'small';
  value: Filter;
  onChange: (filter: Filter) => void;
  activeProjName?: string;
  isHistorical?: boolean;
  showAdvanced?: boolean;
  defaultMode?: 'list' | 'condition';
  excludeNullFromList?: boolean;
  isVertical?: boolean;
  enableQueryObj?: boolean;
  extraOptions?: string[];
  timeRange?: Date[];
}

export const FieldFilterBoxSelect = ({
  value,
  onChange,
  fieldName,
  size,
  activeProjName: maybeActiveProjName,
  isHistorical,
  showAdvanced,
  defaultMode,
  excludeNullFromList,
  isVertical,
  enableQueryObj = false,
  extraOptions,
  timeRange,
}: FieldFilterBoxSelectProps) => {
  const {
    accountEntities: { fieldMap },
    measurements,
  } = useAvContext();

  const activeProjName = useMemo(
    () => maybeActiveProjName || (fieldName ? fieldMap[fieldName]?.mainProjId?.name : undefined),
    [fieldName, fieldMap, maybeActiveProjName]
  );
  const [isOpen, setIsOpen] = useState(false);
  const rawExpression = useMemo(() => filterToRawExpression(value), [value]);

  const filters = useMemo(
    () => value.expression?.stringCondition?.in?.values ?? (rawExpression ? [encodeURIComponent(JSON.stringify(rawExpression))] : []),
    [value, rawExpression]
  );

  const selectedFilterOptions: OptionObject<string>[] = useMemo(() => filters.map(f => ({ label: f || 'Empty', value: f })), [filters]);

  const filterValueLabel = useMemo(() => {
    if (rawExpression && rawExpression?.and?.[0]?.operator !== 'in') {
      if (rawExpression.type && isTopKFilter(rawExpression.type)) {
        const { top, dir, value: metric } = rawExpression;
        if (!top || !metric) return 'Filter value';
        const dirLabel = dir === SortDir.DESC ? 'Top' : 'Bottom';
        const metricName = measurements.asObject[metric]?.displayName || metric;
        return `Filter value: ${dirLabel} ${top} by ${metricName}`;
      }
      const operator = rawExpression.and ? 'and' : (rawExpression as { or?: unknown }).or ? 'or' : null;
      if (operator) {
        const operands: { operator: string; value: string }[] = rawExpression[operator];
        const parts = operands.map(({ operator: op, value: v }) => {
          const label = filterOperatorOptions.string.find(({ value: val }) => val === op)?.title ?? op;
          return `${label} "${v}"`;
        });
        return `Filter value: ${parts.join(` ${operator.toUpperCase()} `)}`;
      }
    }
    if (selectedFilterOptions.length > 0) {
      return `Filter value: ${selectedFilterOptions.map(o => o.label).join(', ')}`;
    }
    return 'Filter value';
  }, [rawExpression, selectedFilterOptions, measurements]);

  const handleTagDelete = useCallback(
    (index: number) => {
      if (value.expression?.stringCondition?.in?.values) {
        const newFilterList = value.expression.stringCondition.in.values.filter((_, i) => i !== index);
        onChange({
          ...value,
          expression: {
            ...value.expression,
            stringCondition: newFilterList.length > 0 ? { in: { values: newFilterList } } : { contains: '' },
          },
        });
      }
    },
    [filters, fieldName]
  );

  const getTagProps = useCallback(
    ({ index }: { index: number }) => ({
      onDelete: () => handleTagDelete(index),
    }),
    [handleTagDelete]
  );

  const trigger = useMemo(
    () => (
      <TextInput
        aria-label={filterValueLabel}
        sx={{
          maxWidth: 'none ',
          width: size === 'small' || !isVertical ? '406px' : '182px',
          whiteSpace: 'nowrap',
          '.MuiOutlinedInput-root': { gap: 0.5, pl: 1, cursor: 'pointer' },
          input: { visibility: 'hidden' },
          '.MuiChip-root': {
            height: size === 'xSmall' ? '18px' : '22px',
            '.MuiChip-label': {
              fontSize: size === 'xSmall' ? '12px' : '14px',
            },
          },
        }}
        /* eslint-disable-next-line react/no-unstable-nested-components */
        startAdornment={() =>
          rawExpression && rawExpression?.and?.[0]?.operator !== 'in' ? (
            rawExpression.type && isTopKFilter(rawExpression.type) ? (
              <TopKDisplay
                metric={rawExpression.value}
                top={rawExpression.top}
                dir={rawExpression.dir}
                style={{ ...ellipsis, width: 'calc(100% - 18px)' }}
              />
            ) : (
              <FieldFilterBoxSelectExpression
                rawExpression={rawExpression}
                key={(rawExpression.and || (rawExpression as any).or)?.length || 0}
              />
            )
          ) : selectedFilterOptions.length > 0 ? (
            <MultiSelectTags
              tags={selectedFilterOptions}
              getTagProps={getTagProps}
              size={size}
              getLabelFunc={({ label }: any) => label}
              getValueFunc={({ value }: any) => value}
            />
          ) : (
            'Select value'
          )
        }
        actionIcons={[<ArrowDown style={isOpen ? { transform: 'rotate(180deg)' } : {}} />]}
      />
    ),
    [size, value, rawExpression, selectedFilterOptions, getTagProps, filterValueLabel]
  );

  return (
    <FieldFilter
      filters={{ [fieldName!]: filters }}
      disabled={!fieldName}
      field={fieldName ?? ''}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      isEnabled={!!fieldName}
      onChange={noop}
      onFilterChange={onChange}
      size={size}
      activeProjName={activeProjName}
      trigger={trigger}
      disablePortalInOperatorSelect={false}
      isHistorical={isHistorical}
      showAdvanced={showAdvanced}
      defaultMode={defaultMode}
      excludeNullFromList={excludeNullFromList}
      enableQueryObj={enableQueryObj}
      extraOptions={extraOptions}
      timeRange={timeRange}
    />
  );
};
